import React, { useEffect, useState } from 'react';
import PinField from 'react-pin-field';
import { Button, ButtonV2, Input } from '@asigna/kit';
import { unlinkSafeEmail, updateSafeEmail, verifySafeEmail } from 'api';
import axios from 'axios';
import cx from 'classnames';
import { GradientCheckBox, ModalCloseButton, NotificationCard } from 'components';
import { toast } from 'sonner';
import { useCurrentSafe } from 'store';
import { EmailMultisigSettingsResponse } from 'types';
import { isValidEmail } from 'utils/helper';

interface TransactionCommonProps {
  currentMail: string;
  onClose: (shouldReload?: boolean) => void;
  onSuccess: (response: EmailMultisigSettingsResponse) => void;
}

// TODO split steps into separate components
export const AddEmailDialog: React.FC<TransactionCommonProps> = ({
  currentMail,
  onClose,
  onSuccess,
}) => {
  const { currentSafe } = useCurrentSafe();
  const [currentStep, setCurrentStep] = useState(1);
  const [emailValue, setEmailValue] = useState(currentMail);
  const [isDefaultEmail, setDefaultEmail] = useState(true);
  const [emailPinCode, setEmailPinCode] = useState('');
  const [resendTimerValue, setResendTimerValue] = useState(0);
  const [isConfirm, setConfirm] = useState(false);
  const [errorConfirm, setErrorConfirm] = useState('');
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (resendTimerValue > 0) {
      const interval = setInterval(() => {
        setResendTimerValue((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [resendTimerValue]);

  const handleGoBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else {
      onClose();
    }
  };

  const handleProceed = async () => {
    setLoading(true);
    await updateSafeEmail(
      emailValue,
      currentSafe?._id || '',
      Boolean(currentMail) ? 'replace' : 'set',
      isDefaultEmail
    ).then(() => {
      // console.log(res);
      setCurrentStep(2);
      setResendTimerValue(60);
      toast(
        () => (
          <NotificationCard
            title="Email verification"
            message="Confirmation code was sent successfully"
            type="success"
          />
        ),
        {
          className: 'w-full',
        }
      );
      setLoading(false);
    });
  };

  const handleResend = async () => {
    await updateSafeEmail(
      emailValue.trim(),
      currentSafe?._id || '',
      Boolean(currentMail) ? 'replace' : 'set',
      isDefaultEmail
    ).then((res) => {
      toast(
        () => (
          <NotificationCard
            title="Email verification"
            message="Confirmation code was sent successfully"
            type="success"
          />
        ),
        {
          className: 'w-full',
        }
      );
      setResendTimerValue(60);
    });
  };

  const checkPinCode = async () => {
    const isReplace = Boolean(currentMail);

    await verifySafeEmail(
      emailValue,
      emailPinCode,
      currentSafe?._id || '',
      isReplace ? 'replace' : 'set',
      isDefaultEmail
    ).then((response) => {
      toast(
        () => (
          <NotificationCard
            title={isReplace ? 'Email changed' : 'Email added'}
            message={`Email address ${emailValue} added successfully`}
            type="success"
          />
        ),
        {
          className: 'w-full',
        }
      );
      onSuccess(response);
      onClose();
    });
  };

  useEffect(() => {
    setErrorConfirm('');

    if (emailPinCode.length !== 6) {
      return;
    }

    const validatePinCode = async () => {
      setConfirm(false);
      setLoading(true);

      try {
        await checkPinCode().then((res) => {
          setConfirm(true);
        });
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const backendMessage = error.response?.data?.message;
          if (backendMessage) {
            setErrorConfirm(backendMessage);
          } else {
            console.error('Error creating subscription order:', error.message);
            setErrorConfirm('Error verifying');
          }
        } else {
          console.error('Unknown error:', error);
          setErrorConfirm('Error verifying');
        }
      } finally {
        setLoading(false);
      }
    };

    validatePinCode();
  }, [emailPinCode.length]);

  const handleDeleteEmail = () => {
    unlinkSafeEmail(currentMail, currentSafe?._id || '').then(() => onClose(true));
  };

  const isSecondStepDisabled =
    emailValue.length === 0 ||
    !isValidEmail(emailValue) ||
    (currentMail && currentMail === emailValue.trim());

  return (
    <div className="flex flex-col gap-32">
      <div className="flex items-center justify-between">
        <p className="text-r20">
          {currentStep === 1
            ? Boolean(currentMail)
              ? 'Change email'
              : 'Add email'
            : 'Verify email'}
        </p>
        {/* TODO Icon Button */}
        <ModalCloseButton onClick={onClose} />
      </div>

      {currentStep === 1 && (
        <div className="flex w-full flex-col gap-24">
          <div className="flex w-full flex-col items-start gap-16">
            <Input
              label="Enter email"
              value={emailValue}
              placeholder="email@example.com"
              type="email"
              onChange={(e) => {
                setEmailValue(e.target.value);
              }}
              size="xs"
            />
            <div className="flex items-center gap-12">
              <GradientCheckBox
                checked={isDefaultEmail}
                onChange={() => setDefaultEmail((prevState) => !prevState)}
              />
              <span className="text-r12">Set as default</span>
            </div>
            {Boolean(currentMail) && (
              <ButtonV2 color="secondary" onClick={handleDeleteEmail}>
                Delete email
              </ButtonV2>
            )}
          </div>
        </div>
      )}
      {currentStep === 2 && (
        <div className="flex w-full flex-col gap-24 items-center">
          <div className="flex w-full flex-col gap-12 items-center">
            <span className="text-r14">Enter the verification code we sent on {emailValue}</span>
            <button
              className={cx('text-r14', resendTimerValue > 0 ? 'text-regular' : 'text-primary')}
              onClick={handleResend}
              disabled={resendTimerValue > 0}
            >
              Resend
              {resendTimerValue > 0 ? ` (${resendTimerValue})` : ''}
            </button>
          </div>
          <div className="flex w-full gap-8 items-center justify-center">
            <PinField
              length={6}
              onChange={setEmailPinCode}
              className={cx(
                'flex items-center justify-center rounded-[8px] w-56 h-48 border-[.5px] bg-black-3 text-center text-r20',
                'border-[#2A2B30] active:outline-none focus-visible:outline-none active:border-primary focus-visible:border-primary'
              )}
            />
          </div>
          <span className="text-r14 text-error">{errorConfirm}</span>
        </div>
      )}

      <div className="flex items-center gap-16 w-full">
        <Button
          message={currentStep === 1 ? 'Cancel' : 'Back'}
          onClick={handleGoBack}
          colorType="secondary"
          fullWidth
        />
        <Button
          message="Proceed"
          onClick={handleProceed}
          colorType="primary"
          fullWidth
          disabled={
            (currentStep === 1 && isSecondStepDisabled) ||
            (currentStep === 2 && !isConfirm) ||
            isLoading
          }
          leftIcon={isLoading ? '/loading-gif.gif' : undefined}
        />
      </div>
    </div>
  );
};
